import React, {useState} from 'react';
import {toast} from 'sonner';
import {network} from '../lib/network';
import {UtilitySchemas} from '@plunk/shared';
import {NextSeo} from 'next-seo';
import {Footer, Modal, Navbar} from '../components';
import {motion} from 'framer-motion';
import {Editor} from '../components/MarkdownEditor';

/**
 *
 */
export default function Index() {
  const [value, setValue] = useState('');

  const [result, setResult] = useState<{score: number; reasons: string[]} | null>();
  const [showModal, setShowModal] = useState(false);

  const check = () => {
    toast.promise(
      network.fetch<{score: number; reasons: string[]}, typeof UtilitySchemas.convert>('POST', '/utils/spam', {
        markdown: value,
      }),
      {
        loading: 'Checking for spam',
        success: result => {
          setResult(result);
          setShowModal(true);

          return 'Checked for spam';
        },
        error: 'Error checking for spam',
      },
    );
  };

  return (
    <>
      <NextSeo
        title={'Spam checker | The Open-Source Email Platform'}
        description={'Check your email content for triggers that may cause your email to be marked as spam'}
        openGraph={{
          title: 'Spam checker | The Open-Source Email Platform',
          description: 'Check your email content for triggers that may cause your email to be marked as spam',
        }}
        additionalMetaTags={[
          {
            property: 'title',
            content: 'Spam checker | The Open-Source Email Platform',
          },
        ]}
      />
      <Modal
        title={'Your results'}
        description={'Here are the results of your spam check'}
        type={'info'}
        isOpen={showModal}
        onAction={() => {
          setResult(null);
          setShowModal(false);
        }}
        onToggle={() => {
          setResult(null);
          setShowModal(false);
        }}
      >
        {!result ? (
          <>
            <div className={'my-6 max-w-md text-sm'}>
              <p className={'text-neutral-600'}>Whoa! Something went wrong, please try again later!</p>
            </div>
          </>
        ) : (
          <>
            <div className={'my-6 max-w-md text-sm'}>
              <p className={'text-neutral-600'}>{result.score * 10}% chance of spam</p>
              <div className="my-3 h-2.5 w-full rounded-full bg-neutral-200">
                <div
                  className={`h-2.5 rounded-full ${
                    result.score >= 7 ? 'bg-red-500' : result.score >= 4 ? 'bg-yellow-500' : 'bg-green-500'
                  }`}
                  style={{width: `${result.score * 10}%`}}
                ></div>
              </div>

              {result.reasons.length > 0 && (
                <div className={'text-neutral-600'}>
                  <p className={'mt-3'}>Reasons</p>
                  <ul className={'list-inside list-disc'}>
                    {result.reasons.map(reason => (
                      <li>{reason}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </>
        )}
      </Modal>
      <Navbar />

      <div className={'mx-auto max-w-7xl px-8 sm:px-0'}>
        <main>
          <div className={'mb-4 flex flex-col items-center justify-center gap-y-2'}>
            <div className={'mt-6 text-center'}>
              <h1 className="text-4xl font-bold text-neutral-800">Spam checker</h1>
              <p className="mx-auto mt-2 max-w-lg text-neutral-500">
                Check your email content for triggers that may cause your email to be marked as spam
              </p>
            </div>
          </div>

          <div className={'mx-auto w-full max-w-3xl p-6'}>
            <Editor value={value} onChange={val => setValue(val)} />
          </div>

          <div className={'mt-9 flex items-center justify-center'}>
            <motion.button
              onClick={() => check()}
              whileHover={{scale: 1.05}}
              whileTap={{scale: 0.9}}
              className={'h-full rounded-lg bg-neutral-800 px-12 py-3 text-sm font-semibold text-white'}
            >
              Check for spam
            </motion.button>
          </div>
        </main>
      </div>

      <Footer />
    </>
  );
}
